import {
  KpiDashboardDefinition,
  KpiOperatorSettingRow,
  KpiOperatorSettings,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { normalizeSearchText } from '../../../util/search-text'
import {
  calculateScoreAverage,
  compareNullableNumbers,
} from '../operator-list/operator-list.logic'
import {
  OperatorMetricViewModel,
  buildEmptyOperatorMetricViewModels,
  buildOperatorMetricViewModels,
  calculateOperatorActualRate,
  calculateOperatorGap,
  formatPeriod,
} from '../operator-detail/operator-detail.logic'
import {
  KeyActionViewModel,
  buildKeyActionViewModels,
} from '../key-action-selector/key-action-selector.logic'

export type KeyActionSettingsSort =
  | 'default'
  | 'actual-asc'
  | 'actual-desc'
  | 'gap'

export interface KeyActionSettingsRowViewModel {
  row: KpiOperatorSettingRow
  metaText: string
  metrics: OperatorMetricViewModel[]
  selectedKeyActions: KeyActionViewModel[]
  averageActual: number | null
  averageGap: number | null
}

/** 氏名を正規化して部分一致検索し、API 順を保つ。 */
export function filterKeyActionSettingsRows(
  rows: readonly KpiOperatorSettingRow[],
  searchQuery: string,
): KpiOperatorSettingRow[] {
  const normalizedQuery = normalizeSearchText(searchQuery)
  if (normalizedQuery === '') {
    return [...rows]
  }

  return rows.filter((row) =>
    normalizeSearchText(row.userName).includes(normalizedQuery),
  )
}

/** 検索中は該当人数と全人数、未検索時は全人数を表示する。 */
export function formatKeyActionOperatorCount(
  filteredCount: number,
  totalCount: number,
  searchQuery: string,
): string {
  return normalizeSearchText(searchQuery) === ''
    ? `${totalCount}名`
    : `${filteredCount} / ${totalCount}名`
}

/** API の設定一覧を KPI・KeyAction 表示用の行モデルに変換する。 */
export function buildKeyActionSettingsRows(
  dashboard: KpiDashboardDefinition,
  settings: KpiOperatorSettings,
): KeyActionSettingsRowViewModel[] {
  return settings.rows.map((row) => ({
    row,
    metaText: createOperatorMetaText(settings.latestPeriod, row.roleTitle),
    metrics:
      row.metrics.length === 0
        ? buildEmptyOperatorMetricViewModels(dashboard.items)
        : buildOperatorMetricViewModels(dashboard.items, row.metrics),
    selectedKeyActions: buildKeyActionViewModels(
      dashboard.keyActions,
      row.keyActionScores,
      row.selectedKeyActionIds,
    ).filter(({ selected }) => selected),
    averageActual: calculateAverageActual(row),
    averageGap: calculateAverageGap(row),
  }))
}

/** 設定行に含まれる有効な KPI 実績率の平均を算出する。 */
export function calculateAverageActual(
  row: KpiOperatorSettingRow,
): number | null {
  const actualRates = row.metrics
    .map((metric) => calculateOperatorActualRate(metric))
    .filter((value): value is number => value != null)

  return calculateScoreAverage(actualRates)
}

/** 設定行に含まれる有効な KPI 目標乖離の平均を算出する。 */
export function calculateAverageGap(row: KpiOperatorSettingRow): number | null {
  const gaps = row.metrics
    .map((metric) => calculateOperatorGap(metric))
    .filter((value): value is number => value != null)

  return calculateScoreAverage(gaps)
}

/** KeyAction 設定行を指定条件で並び替え、null 値は末尾に保つ。 */
export function sortKeyActionSettingsRows(
  rows: readonly KeyActionSettingsRowViewModel[],
  sort: KeyActionSettingsSort,
): KeyActionSettingsRowViewModel[] {
  if (sort === 'default') {
    return [...rows]
  }

  return [...rows].sort((left, right) => {
    if (sort === 'actual-asc') {
      return compareNullableNumbers(
        left.averageActual,
        right.averageActual,
        'asc',
      )
    }
    if (sort === 'actual-desc') {
      return compareNullableNumbers(
        left.averageActual,
        right.averageActual,
        'desc',
      )
    }

    return compareNullableNumbers(left.averageGap, right.averageGap, 'asc')
  })
}

function createOperatorMetaText(
  latestPeriod: string | null | undefined,
  roleTitle: string | null | undefined,
): string {
  if (typeof latestPeriod === 'undefined') {
    throw new Error('Latest KPI period is missing')
  }
  if (typeof roleTitle === 'undefined') {
    throw new Error('Operator role title is missing')
  }

  const periodText =
    latestPeriod == null ? '実績未入稿' : formatPeriod(latestPeriod)
  return roleTitle == null ? periodText : `${periodText}・${roleTitle}`
}
